import React from 'react';
import {
    TableRow,
    TableCell,
    Checkbox,
    IconButton,
    Typography,
    Box,
    Stack,
    Tooltip,
    Avatar,
} from '@mui/material';
import { MoreVert as MoreIcon, ContentCopyOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';

import useCurrency from '../../../hooks/common/useCurrency';
import CustomerStatusBadge from './CustomerStatusBadge';

const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
        toast.success(`${label}: ${text}`);
    });
};

const CustomersTableRow = ({
    item,
    index = 0,
    isSelected,
    onRowCheckboxClick,
    onMenuOpen,
}) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const initials =
        (item?.name || '')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((s) => s[0])
            .join('')
            .toUpperCase() || '?';

    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox">
                <Checkbox checked={isSelected} onClick={(e) => onRowCheckboxClick(e, item.id)} />
            </TableCell>

            {/* S/N */}
            <TableCell align="start">{index + 1}</TableCell>

            {/* Name */}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>{initials}</Avatar>
                    <Typography
                        component={RouterLink}
                        to={`/customers/${item.id}`}
                        variant="body2"
                        fontWeight="bold"
                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                    >
                        {item.name || '-'}
                    </Typography>
                </Box>
            </TableCell>

            {/* Email */}
            <TableCell>
                {item.email ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ direction: 'ltr' }}>
                            {item.email}
                        </Typography>
                        <Tooltip title={isArabic ? 'نسخ' : 'Copy'}>
                            <IconButton size="small" onClick={() => copy(item.email, isArabic ? 'تم النسخ' : 'Copied')}>
                                <ContentCopyOutlined sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ) : '-'}
            </TableCell>

            {/* Phone */}
            <TableCell>
                {item.phone ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ direction: 'ltr' }}>
                            {item.phone}
                        </Typography>
                        <Tooltip title={isArabic ? 'نسخ' : 'Copy'}>
                            <IconButton size="small" onClick={() => copy(item.phone, isArabic ? 'تم النسخ' : 'Copied')}>
                                <ContentCopyOutlined sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ) : '-'}
            </TableCell>

            {/* Orders count */}
            <TableCell align="center">
                <Typography variant="body2" fontWeight="medium">{item.orders_count ?? 0}</Typography>
            </TableCell>

            {/* Total Spent */}
            <TableCell align="center">
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {formatPrice(item.total_spent ?? 0)}
                </Typography>
            </TableCell>

            {/* Status */}
            <TableCell align="center">
                <CustomerStatusBadge status={item.status} />
            </TableCell>

            {/* Created at */}
            <TableCell align="center">
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                    {item.created_at ? dayjs(item.created_at).format('YYYY-MM-DD') : '-'}
                </Typography>
            </TableCell>

            {/* Actions */}
            <TableCell align="center">
                <IconButton size="small" onClick={(e) => onMenuOpen(e, item)}>
                    <MoreIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default CustomersTableRow;
